import { Upload, Mail, CheckCircle, Users, Clock, FileText, QrCode, ExternalLink, Phone, Calendar, Globe, DollarSign, TrendingUp, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useOnboardingMetrics } from '../../hooks/useOnboardingMetrics';
import { useOnboardingTasks } from '../../hooks/useOnboardingTasks';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useSaaSSubscription } from '../../hooks/useSaaSSubscription';
import { useGoogleBusinessSummary } from '../../hooks/useGoogleBusinessSummary';
import { useBillingReadiness } from '../../hooks/useBillingReadiness';
import { supabase } from '../../lib/supabase';

/**
 * Onboarding Dashboard Component
 * 
 * Displays comprehensive RollOps onboarding progress including:
 * - Student onboarding metrics (uploaded, invited, accepted)
 * - Setup progress (billing, Google Business Profile)
 * - Trial status with days remaining
 * - Quick action links (upload students, waivers, QR codes)
 * - Weekly webinar training task
 * - Support links
 */
export function OnboardingDashboard() {
  const navigate = useNavigate();
  const { data: companyId } = useCurrentCompany();
  const { data: metrics, isLoading } = useOnboardingMetrics();
  const { data: onboardingTasks, isLoading: isLoadingTasks } = useOnboardingTasks();
  const { data: saasSubscription } = useSaaSSubscription();
  const { data: googleBusinessSummary } = useGoogleBusinessSummary();
  const { data: billingReadiness } = useBillingReadiness();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [billingSetupComplete, setBillingSetupComplete] = React.useState<boolean | null>(null);
  const [hasWebsiteUrl, setHasWebsiteUrl] = React.useState(false);
  const [_enableBilling, setEnableBilling] = React.useState<boolean | null>(null);
  const [isRequestingWebinar, setIsRequestingWebinar] = React.useState(false);
  const [webinarRequestSuccess, setWebinarRequestSuccess] = React.useState(false);

  // Check billing setup status
  React.useEffect(() => {
    const checkBillingSetup = async () => {
      if (!companyId) return;
      try {
        const { data, error } = await (supabase as any)
          .from('subscription_billing_settings')
          .select('id, payment_provider')
          .eq('company_id', companyId)
          .maybeSingle();

        if (!error && data?.payment_provider) {
          setBillingSetupComplete(true);
        } else {
          setBillingSetupComplete(false);
        }
      } catch {
        setBillingSetupComplete(false);
      }
    };
    checkBillingSetup();
  }, [companyId]);

  // Check website URL
  // Use stable reference to googleBusinessSummary place_id to prevent infinite loops
  const googleBusinessPlaceId = googleBusinessSummary?.place_id;
  React.useEffect(() => {
    const checkWebsite = async () => {
      if (!companyId) return;
      try {
        const { data, error } = await (supabase as any)
          .from('companies')
          .select('website_url, place_id, enable_billing')
          .eq('id', companyId)
          .single();

        if (!error) {
          if (data?.website_url || data?.place_id) {
            setHasWebsiteUrl(true);
          } else {
            setHasWebsiteUrl(false);
          }
          setEnableBilling(data?.enable_billing === true);
        } else {
          setHasWebsiteUrl(false);
          setEnableBilling(false);
        }
      } catch {
        setHasWebsiteUrl(false);
        setEnableBilling(false);
      }
    };
    checkWebsite();
  }, [companyId, googleBusinessPlaceId]);

  // Calculate trial days remaining
  const trialDaysRemaining = React.useMemo(() => {
    if (!saasSubscription?.trial_end_date || saasSubscription.status !== 'trialing') {
      return null;
    }
    const endDate = new Date(saasSubscription.trial_end_date);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [saasSubscription]);

  // Calculate setup progress (available for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _setupProgress = React.useMemo(() => {
    let completed = 0;
    const total = 2;

    // Billing setup
    if (billingSetupComplete) completed++;

    // Google Business Profile / Website
    if (hasWebsiteUrl || googleBusinessSummary) completed++;

    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [billingSetupComplete, hasWebsiteUrl, googleBusinessSummary]);

  if (isLoading || isLoadingTasks) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Users className="w-5 h-5 text-rollops-accent" />
            RollOps onboarding progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421] mx-auto" />
            <p className="mt-2 text-text-tertiary text-sm">Loading metrics...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalStudents = (metrics?.uploaded || 0) + (metrics?.invited || 0) + (metrics?.accepted || 0);
  const hasAnyContent = totalStudents > 0 || trialDaysRemaining !== null || !onboardingTasks?.allCompleted;

  // Always show the card if there's any onboarding content
  if (!hasAnyContent) {
    return null;
  }

  // Billing enablement CTA removed - billing is no longer feature-gated
  const showBillingEnablementCTA = false;

  return (
    <Card className="bg-[#221F1F] border-[#504A4A]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Users className="w-5 h-5 text-rollops-accent" />
            RollOps onboarding progress
          </CardTitle>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-text-tertiary hover:text-text-primary transition-colors"
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-6">
          {/* Trial Status - Show at top */}
          {trialDaysRemaining !== null && (
            <div className="bg-[#363232] rounded-lg p-4 border border-[#504A4A]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Trial {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining
                    </p>
                    <p className="text-xs text-text-tertiary">Upgrade to continue</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/subscriptions-plans/rollops')}
                  className="text-xs text-text-primary hover:text-text-secondary underline font-medium"
                >
                  Sign up now
                </button>
              </div>
            </div>
          )}

          {/* Onboarding Tasks */}
          {onboardingTasks && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-text-primary">Onboarding Tasks</h3>
                <p className="text-xs text-text-tertiary">
                  {onboardingTasks.completedCount} of {onboardingTasks.totalCount} completed
                </p>
              </div>
              <div className="space-y-2">
                {onboardingTasks.tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      task.completed
                        ? 'bg-[#1a3a1a]/30 border-green-500/30'
                        : 'bg-[#363232] border-[#504A4A]'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {task.completed ? (
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-[#504A4A] flex-shrink-0" />
                      )}
                      <span
                        className={`text-sm ${
                          task.completed ? 'text-text-secondary line-through' : 'text-text-primary'
                        }`}
                      >
                        {task.label}
                      </span>
                    </div>
                    {!task.completed && task.route && (
                      <button
                        onClick={() => task.route && navigate(task.route)}
                        className="text-xs text-text-primary hover:text-text-secondary underline font-medium ml-2"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        {/* Student Onboarding Cards */}
        {totalStudents > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Draft Students Uploaded */}
            <div
              onClick={() => navigate('/students?tab=draft&status=draft')}
              className="bg-[#363232] rounded-lg p-4 border border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#9B1421]/20 rounded-lg">
                  <Upload className="w-5 h-5 text-text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-text-secondary">Uploaded</p>
                  <p className="text-2xl font-bold text-text-primary">{metrics?.uploaded || 0}</p>
                </div>
              </div>
              <p className="text-xs text-text-tertiary mt-2">
                Draft students not yet invited
              </p>
            </div>

            {/* Invited */}
            <div
              onClick={() => navigate('/students?tab=draft&status=invited')}
              className="bg-[#363232] rounded-lg p-4 border border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#9B1421]/20 rounded-lg">
                  <Mail className="w-5 h-5 text-text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-text-secondary">Invited</p>
                  <p className="text-2xl font-bold text-text-primary">{metrics?.invited || 0}</p>
                </div>
              </div>
              <p className="text-xs text-text-tertiary mt-2">
                Invitations sent, awaiting acceptance
              </p>
            </div>

            {/* Accepted Invite */}
            <div
              onClick={() => navigate('/students?tab=draft&status=registered')}
              className="bg-[#363232] rounded-lg p-4 border border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#9B1421]/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-text-secondary">Accepted</p>
                  <p className="text-2xl font-bold text-text-primary">{metrics?.accepted || 0}</p>
                </div>
              </div>
              <p className="text-xs text-text-tertiary mt-2">
                Invites accepted, pending registration
              </p>
            </div>
          </div>
        )}

        {/* Billing Enablement CTA - Show when ready */}
        {showBillingEnablementCTA && billingReadiness && (
          <div className="bg-gradient-to-r from-[#9B1421]/20 to-[#7A0F1A]/20 border-2 border-[#9B1421] rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#9B1421]/30 rounded-lg flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Ready to Enable RollOps Billing
                </h3>
                <p className="text-sm text-text-secondary mb-4">
                  You&apos;ve completed all recommended tasks and have {billingReadiness.enrolledStudents} students enrolled in {billingReadiness.activeSubscriptions} active subscription{billingReadiness.activeSubscriptions !== 1 ? 's' : ''}. 
                  Enable RollOps billing to start saving on processing fees and increase your revenue.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-[#221F1F]/50 rounded-lg p-3 border border-[#504A4A]">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-text-tertiary">Estimated Yearly Savings</span>
                    </div>
                    <p className="text-xl font-bold text-text-primary">
                      ${billingReadiness.estimatedYearlySavings.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="bg-[#221F1F]/50 rounded-lg p-3 border border-[#504A4A]">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-text-tertiary">Estimated Revenue Increase</span>
                    </div>
                    <p className="text-xl font-bold text-text-primary">
                      ${billingReadiness.estimatedYearlyRevenueIncrease.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/billing/settings')}
                  className="w-full md:w-auto px-6 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-text-primary font-semibold rounded-lg transition-colors"
                >
                  Enable RollOps Billing →
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Quick Links Section */}
        <div className="border-t border-[#504A4A] pt-4">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/upload-student-data')}
              className="flex items-center gap-2 p-3 bg-[#363232] rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors text-left"
            >
              <Upload className="w-4 h-4 text-text-primary flex-shrink-0" />
              <span className="text-sm text-text-primary">Upload Students</span>
            </button>
            <button
              onClick={() => navigate('/damage-waivers/upload')}
              className="flex items-center gap-2 p-3 bg-[#363232] rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors text-left"
            >
              <FileText className="w-4 h-4 text-text-primary flex-shrink-0" />
              <span className="text-sm text-text-primary">Upload Waivers</span>
            </button>
            <button
              onClick={() => navigate('/qr-codes')}
              className="flex items-center gap-2 p-3 bg-[#363232] rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors text-left"
            >
              <QrCode className="w-4 h-4 text-text-primary flex-shrink-0" />
              <span className="text-sm text-text-primary">Print QR Codes</span>
            </button>
          </div>
        </div>

        {/* Weekly Webinar Training Task */}
        <div className="border-t border-[#504A4A] pt-4">
          <div className="flex items-start gap-3 p-3 bg-[#363232] rounded-lg border border-[#504A4A]">
            <Calendar className="w-5 h-5 text-text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-text-primary mb-1">
                Weekly Training Webinar
              </h3>
              <p className="text-xs text-text-secondary mb-2">
                Learn how to leverage RollOps to its highest potential and troubleshoot issues
              </p>
              {webinarRequestSuccess ? (
                <div className="inline-flex items-center gap-1 text-xs text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  Request submitted! You&apos;ll receive an invitation soon.
                </div>
              ) : (
                <button
                  onClick={async () => {
                    setIsRequestingWebinar(true);
                    setWebinarRequestSuccess(false);
                    try {
                      const { data, error } = await supabase.functions.invoke('request-webinar-training', {
                        body: {},
                      });

                      if (error) {
                        console.error('Edge function error:', error);
                        throw new Error(error.message || 'Failed to submit request');
                      }

                      if (data?.success) {
                        setWebinarRequestSuccess(true);
                      } else {
                        console.error('Edge function returned error:', data);
                        throw new Error(data?.error || 'Failed to submit request');
                      }
                    } catch (error) {
                      console.error('Error requesting webinar training:', error);
                      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                      alert(`Failed to submit webinar request: ${errorMessage}\n\nPlease try again or contact support.`);
                    } finally {
                      setIsRequestingWebinar(false);
                    }
                  }}
                  disabled={isRequestingWebinar}
                  className="inline-flex items-center gap-1 text-xs text-text-primary hover:text-text-secondary underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRequestingWebinar ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Schedule session
                      <ExternalLink className="w-3 h-3" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Support Links */}
        <div className="border-t border-[#504A4A] pt-4">
          <h3 className="text-sm font-semibold text-text-primary mb-2">Need Help?</h3>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:support@rollops.pro?subject=Support Request"
              className="flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email Support
            </a>
            <span className="text-text-tertiary">•</span>
            <a
              href="tel:+15035551234"
              className="flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Support
            </a>
            <span className="text-text-tertiary">•</span>
            <a
              href="https://docs.rollops.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              <Globe className="w-4 h-4" />
              Documentation
            </a>
          </div>
        </div>
        </CardContent>
      )}
    </Card>
  );
}
